export type PackingMediaPhase =
  | 'PREPARATION'
  | 'INITIALIZATION'
  | 'TRANSFER'
  | 'FINALIZATION'
  | 'ATTACHMENT';
export type PackingMediaFailureCategory =
  | 'NETWORK'
  | 'TIMEOUT'
  | 'STALLED'
  | 'HTTP_REJECTION'
  | 'SERVER_ERROR'
  | 'APPLICATION_REJECTION'
  | 'UNCONFIRMED'
  | 'OTHER';
export type PackingMediaDiagnostic = {
  packageId: number;
  attemptId: string;
  phase: PackingMediaPhase;
  category: PackingMediaFailureCategory;
  uploadReference?: string;
  assetIds?: number[];
  uploadMethod?: string;
  deviceCategory?: string;
  mimeType?: string;
  bytes?: number;
  loaded?: number;
  elapsedMs: number;
  httpStatus?: number;
  applicationCode?: number;
  verification?: 'NOT_CONFIRMED' | 'CHECK_FAILED';
};

const phases = new Set(['PREPARATION', 'INITIALIZATION', 'TRANSFER', 'FINALIZATION', 'ATTACHMENT']);
const categories = new Set([
  'NETWORK',
  'TIMEOUT',
  'STALLED',
  'HTTP_REJECTION',
  'SERVER_ERROR',
  'APPLICATION_REJECTION',
  'UNCONFIRMED',
  'OTHER',
]);
const safeNumber = (value: unknown) =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
const safeId = (value: unknown) =>
  safeNumber(value) && Number(value) > 0 ? Number(value) : undefined;

/** Build from an allowlist; neither raw failures nor inherited HTTP context belongs here. */
export const packingMediaDiagnosticEvent = (input: PackingMediaDiagnostic, portal: string) => {
  if (!safeId(input.packageId)) return undefined;
  const phase = phases.has(input.phase) ? input.phase : 'PREPARATION';
  const category = categories.has(input.category) ? input.category : 'OTHER';
  return {
    message: 'Packing media operation not confirmed',
    level: 'warning' as const,
    fingerprint: ['packing-media', phase, category],
    tags: {
      feature: 'packing-media',
      phase,
      category,
      portal: portal === 'vendor' ? 'nexus' : portal === 'warehouse' ? 'hub' : 'other',
    },
    contexts: {
      packing_media: {
        packageId: input.packageId,
        attemptId:
          /^[a-z0-9]+-[a-z0-9]+$/.test(input.attemptId ?? '') && input.attemptId.length <= 40
            ? input.attemptId
            : undefined,
        uploadReference: /^(?:[0-9a-f]{32}|[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12})$/i.test(
          input.uploadReference ?? '',
        )
          ? input.uploadReference
          : undefined,
        assetIds: Array.isArray(input.assetIds)
          ? [...new Set(input.assetIds.filter(id => safeId(id)))].slice(0, 5)
          : undefined,
        uploadMethod: ['RECORDED', 'FILE_UPLOAD'].includes(input.uploadMethod ?? '')
          ? input.uploadMethod
          : 'UNKNOWN',
        deviceCategory: ['MOBILE', 'TABLET', 'DESKTOP'].includes(input.deviceCategory ?? '')
          ? input.deviceCategory
          : 'UNKNOWN',
        mimeType: /^video\/[a-z0-9.+-]{1,40}$/i.test(input.mimeType ?? '')
          ? input.mimeType
          : undefined,
        bytes: safeNumber(input.bytes),
        loaded: safeNumber(input.loaded),
        elapsedMs: safeNumber(input.elapsedMs),
        httpStatus:
          safeNumber(input.httpStatus) && input.httpStatus! >= 100 && input.httpStatus! <= 599
            ? input.httpStatus
            : undefined,
        applicationCode:
          safeNumber(input.applicationCode) &&
          input.applicationCode! >= 1000 &&
          input.applicationCode! <= 9999
            ? input.applicationCode
            : undefined,
        verification: ['NOT_CONFIRMED', 'CHECK_FAILED'].includes(input.verification ?? '')
          ? input.verification
          : undefined,
      },
    },
  };
};
